/**
 * /settings/guardrails/:policyId — one document, rendered, with its actions
 * (download, delete). Deep-linkable; an id that doesn't resolve (deleted, or
 * someone else's link) redirects to the list rather than rendering an empty
 * shell.
 */
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Modal, Skeleton } from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { MarkdownViewer } from '../../components/MarkdownViewer.js';
import { useMe } from '../../hooks/useMe.js';
import { useUsersListLogic } from '../users/useUsersListLogic.js';
import { CrumbHeader } from './CrumbHeader.js';
import { useDeletePolicyLogic, usePoliciesListLogic } from './useGuardrailsLogic.js';
import { GUARDRAILS_PATH, documentFileName, downloadTextFile, updatedMeta } from './shared.js';

export const PolicyViewPage = () => {
  const { policyId } = useParams();
  const navigate = useNavigate();
  const meQuery = useMe();
  const tenantId = meQuery.data?.tenant.id;
  const policiesQuery = usePoliciesListLogic(tenantId);
  const usersQuery = useUsersListLogic(tenantId);
  const deleteLogic = useDeletePolicyLogic(tenantId);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  if (meQuery.isLoading || !tenantId || policiesQuery.isLoading) {
    return (
      <AppShell>
        <Skeleton className="mb-6 h-5 w-64" />
        <Skeleton className="mb-4 h-7 w-72" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </AppShell>
    );
  }

  const policy = (policiesQuery.data ?? []).find((p) => p.id === policyId);
  if (!policy) return <Navigate to={GUARDRAILS_PATH} replace />;

  const users = usersQuery.data?.users ?? [];

  const handleDelete = async () => {
    try {
      await deleteLogic.delete(policy.id);
      navigate(GUARDRAILS_PATH);
    } catch {
      // Surfaced via deleteLogic.error.
    }
  };

  return (
    <AppShell>
      <CrumbHeader title={policy.name} onBack={() => navigate(GUARDRAILS_PATH)} />
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="text-ink truncate text-2xl font-bold">{policy.name}</h2>
          <Badge variant={policy.isActive ? 'success' : 'neutral'}>
            {policy.isActive ? 'ACTIVE' : 'DISABLED'}
          </Badge>
        </div>
        <div className="flex flex-none gap-2">
          <Button
            onClick={() => navigate(`${GUARDRAILS_PATH}/add?to=${encodeURIComponent(policy.id)}`)}
          >
            Add rule
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate(`${GUARDRAILS_PATH}/${encodeURIComponent(policy.id)}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="secondary"
            onClick={() => downloadTextFile(documentFileName(policy.name), policy.document)}
          >
            Download
          </Button>
          <Button variant="secondary" onClick={() => setConfirmingDelete(true)}>
            Delete
          </Button>
        </div>
      </div>
      <p className="text-ink-subtle mb-4 text-xs">{updatedMeta(policy, users)}</p>

      {deleteLogic.error && (
        <p className="text-danger mb-3 text-xs font-semibold">
          Failed to delete: {deleteLogic.error.message}
        </p>
      )}

      <div className="border-outline bg-surface-elevated rounded-xl border px-6 py-5">
        <MarkdownViewer variant="policy">{policy.document}</MarkdownViewer>
      </div>

      {confirmingDelete && (
        <Modal open onOpenChange={() => setConfirmingDelete(false)}>
          <Modal.Header>
            <Modal.Title>Delete {policy.name}?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-ink-muted text-sm">
              The document is deleted and stops being enforced on new conversations. This can&apos;t
              be undone.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setConfirmingDelete(false)}
              disabled={deleteLogic.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => void handleDelete()}
              disabled={deleteLogic.isSubmitting}
            >
              {deleteLogic.isSubmitting ? 'Deleting…' : 'Delete'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </AppShell>
  );
};
